/**
 * Shared NZ-specific input formatters. Apply on input `onBlur` so users can
 * type naturally and the value gets normalised when they tab out.
 */

/**
 * Format a NZ phone number into "(0X) XXX XXXX" for 9-digit landlines or
 * "(0XX) XXX XXXX[X]" for mobile numbers starting with 02. Falls back to the
 * original input if it can't be confidently formatted (so international
 * numbers and short extensions aren't mangled).
 */
export function formatNzPhone(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  // International with + prefix — never mangle. The backend will normalise
  // to E.164 via Google libphonenumber and stamp `phone_mobile_e164`.
  if (s.startsWith('+')) return s;
  const digits = s.replace(/\D/g, '');
  // Mobile (11 digits): 021 0232 8311 — older 021 numbers with longer trailing portion.
  if (digits.startsWith('02') && digits.length === 11) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 7)} ${digits.slice(7)}`;
  }
  // Mobile: 02X XXX XXXX or 02X XXX XXXXX  (9 or 10 digits)
  if (digits.startsWith('02') && (digits.length === 9 || digits.length === 10)) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  // Landline: 0X XXX XXXX (9 digits) — covers 07, 09, 03, 04 etc.
  if (digits.length === 9 && digits.startsWith('0')) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 5)} ${digits.slice(5)}`;
  }
  return raw;
}

/**
 * Format an IRD GST number as "XXX-XXX-XXX" (9 digits, hyphen-separated).
 * NZ legacy GST numbers are 8 digits; we pad-left so old 8-digit values
 * become "0XX-XXX-XXX". Falls back to original input if not 8/9 digits.
 */
export function formatGstNumber(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 8) {
    const padded = '0' + digits;
    return `${padded.slice(0, 3)}-${padded.slice(3, 6)}-${padded.slice(6)}`;
  }
  return raw;
}
